import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import Bind from 'lodash-decorators/bind';
import { observer } from 'mobx-react';

import messages from './NewsListView.intl';

import styles from './NewsArticleView.module.scss';

import { LOAD_STATES, ANCHOR } from 'common/consts/common';
import { Container, Title } from 'common/components/Layout/Layout';
import { generateYearsFilterArray } from 'utils/date-utils';
import PrimaryButtonComponent from 'common/components/PrimaryButtonComponent/PrimaryButtonComponent';
import Filters from 'common/components/Filters/Filters';
import ArticleHeader from './components/Article/ArticleHeader/ArticleHeader';
import NewsListItem from './components/NewsListItemComponent/NewsListItemComponent';
import FeaturedNews from './components/FeaturedNews/FeaturedNews';
import PageNotFound from 'views/PageNotFound/PageNotFoundView';
import LanguageService from 'services/Language.service';
import NewsStore from 'stores/News.store';
import ContentStore from 'stores/Content.store';
import InjectMetaTags from 'common/components/InjectMetaTags/InjectMetaTags';

import Placeholder from 'common/components/Placeholder/Placeholder';
import ArticleBig from 'common/components/Placeholder/components/ArticleBig/ArticleBig';
import ArticleSmall from 'common/components/Placeholder/components/ArticleSmall/ArticleSmall';
import ArticleSideText from 'common/components/Placeholder/components/ArticleSideText/ArticleSideText';

import { DEFAULT_LIMIT as HOMEPAGE_ARTICLES_LIMIT } from 'views/Home/Sections/NewsAndEvents/NewsAndEvents';

import { setStateAsync } from 'common/utils/utils';

const DEFAULT_LIMIT = 10;
const DEFAULT_SKIP = 0;

@injectIntl
@withRouter
@observer
export default class NewsArticleView extends Component {
  constructor(props) {
    super(props);
    this.initFetch = true;
    this.stateKey = this.props.location.key;
    this.currentLanguage = LanguageService.getLang();
    this.filtersList = generateYearsFilterArray({
      first: {
        text: this.props.intl.formatMessage(messages.filterAll),
        value: ''
      },
      from: 2017
    });
    this.selectedFilter = this.props.match.params.category
      ? this.filtersList.find(
          filter => filter.value === this.props.match.params.category
        )
      : this.filtersList[0];

    if (!this.selectedFilter) {
      return;
    }

    this.state = {
      activeFilter: this.selectedFilter,
      loadState: LOAD_STATES.LOADED,
      latestNews: {
        criteria: {
          publishYear: this.selectedFilter.value
        },
        pagination: {
          skip: DEFAULT_SKIP,
          limit: DEFAULT_LIMIT
        },
        items: [],
        endOfCollection: false
      }
    };

    if (this.stateKey === NewsStore.latestNewsSettings.historyStateKey) {
      const {
        criteria,
        pagination,
        endOfCollection
      } = NewsStore.latestNewsSettings;

      Object.assign(this.state.latestNews, {
        criteria: { ...criteria },
        pagination: {
          skip: 0,
          limit: pagination.skip
        },
        endOfCollection: endOfCollection
      });
    }
  }

  componentDidMount() {
    if (!this.selectedFilter) {
      return;
    }
    this.fetchLatest();
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    const { category } = this.props.match.params;

    if (category !== prevProps.match.params.category) {
      this.selectedFilter = category
        ? this.filtersList.find(filter => filter.value === category)
        : this.filtersList[0];

      const newState = {
        criteria: {
          publishYear: this.selectedFilter.value
        },
        pagination: {
          limit: DEFAULT_LIMIT,
          skip: 0
        },
        items: [],
        endOfCollection: false
      };

      await setStateAsync.call(this, {
        latestNews: newState,
        activeFilter: this.selectedFilter
      });
      this.fetchLatest();
    }
  }

  componentWillUnmount() {
    if (!this.selectedFilter) {
      return;
    }

    const { criteria, pagination, endOfCollection } = this.state.latestNews;
    NewsStore.latestNewsSettings = {
      historyStateKey: this.stateKey,
      criteria: { ...criteria },
      pagination: { ...pagination },
      endOfCollection: endOfCollection
    };
  }

  render() {
    if (!this.selectedFilter) {
      return <PageNotFound />;
    }

    const {
      activeFilter,
      latestNews: { items, endOfCollection }
    } = this.state;

    const latestNews = [...items];

    const mostLatestNews = latestNews.shift();

    return (
      <div className={styles.NewsArticleView}>
        {ContentStore.isLoaded && (
          <InjectMetaTags {...ContentStore.content.newsSEO} />
        )}
        <Container>
          <div className={styles.MainHeading}>
            <Title
              title={this.props.intl.formatMessage(messages.heading)}
              className={styles.MainHeadingWithFilters}
            />
            <Filters
              activeFilter={activeFilter}
              onSelect={this.filterChanged}
              filters={this.filtersList}
              className={styles.Filters}
            />
          </div>
        </Container>
        <Container>
          <div className={`${styles.ContainerArticle} `}>
            <div className={styles.ReturnDiv}>
              <span className={styles.Return}>
                {this.props.intl.formatMessage(messages.newest)}
              </span>
            </div>
            <Placeholder component={<ArticleBig />} ready={this.isLoaded()}>
              {mostLatestNews && (
                <ArticleHeader isLink={true} news={mostLatestNews} />
              )}
            </Placeholder>
            <Placeholder
              component={<ArticleSideText />}
              repeat={2}
              ready={this.isLoaded() || !!latestNews.length}
            >
              {latestNews &&
                latestNews.map(news => (
                  <NewsListItem
                    key={news.id}
                    news={news}
                    className={styles.MarginTop}
                  />
                ))}
            </Placeholder>
            <div className={styles.ButtonContainer}>
              {!endOfCollection && (
                <PrimaryButtonComponent
                  onClick={this.fetchLatest}
                  isBusy={!this.isLoaded()}
                  text={this.props.intl.formatMessage(messages.button)}
                />
              )}
            </div>
            {!items.length &&
              this.isLoaded() && (
                <p>{this.props.intl.formatMessage(messages.noResults)}</p>
              )}
          </div>
          <Placeholder
            component={<ArticleSmall className={styles.ArticleSmall} />}
            repeat={3}
            ready={this.isLoaded()}
          >
            {!!mostLatestNews && (
              <FeaturedNews
                activeNewsID={mostLatestNews.system.id}
                className={styles.SidebarFileToDownload}
              />
            )}
          </Placeholder>
        </Container>
      </div>
    );
  }

  isLoaded() {
    return this.state.loadState === LOAD_STATES.LOADED;
  }

  @Bind
  async fetchLatest() {
    this.setState({ loadState: LOAD_STATES.LOADING });

    const { criteria, pagination, items } = this.state.latestNews;
    const queriedLatestNews = await NewsStore.fetchLatestNews(
      criteria,
      pagination
    );

    const skipHomepageCachedArticles =
      NewsStore.initHomepageFetch.get() && this.initFetch
        ? HOMEPAGE_ARTICLES_LIMIT
        : DEFAULT_SKIP;

    // immmutable
    const newState = {
      criteria: { ...criteria },
      pagination: {
        limit: DEFAULT_LIMIT,
        skip: pagination.skip + pagination.limit + skipHomepageCachedArticles
      },
      items: [...items, ...queriedLatestNews.items],
      endOfCollection: queriedLatestNews.endOfCollection
    };

    this.setState({
      latestNews: newState,
      loadState: LOAD_STATES.LOADED
    });

    this.initFetch = false;
  }

  @Bind
  filterChanged(selectedFilter) {
    const {
      history,
      match: {
        params: { lang }
      }
    } = this.props;

    const langPrefix = lang ? `/${lang}` : '';
    history.push(
      `${langPrefix}${ANCHOR.NEWS[this.currentLanguage]}/${
        selectedFilter.value
      }`
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/News/NewsListView.js